class TimelineProgress extends HTMLElement {
  connectedCallback() {
    this.update = this.updateProgress.bind(this);

    // Measure once layout is stable, then again whenever it can change:
    // section CSS is @imported (may land late), web fonts reflow the items,
    // and the track resizes on orientation change / device toolbar toggles.
    requestAnimationFrame(this.update);
    window.addEventListener("load", this.update);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(this.update);
    }

    if ("ResizeObserver" in window) {
      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(this);
    } else {
      window.addEventListener("resize", this.update);
    }
  }

  disconnectedCallback() {
    window.removeEventListener("load", this.update);
    window.removeEventListener("resize", this.update);
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  updateProgress() {
    const currentDate = new Date();
    const dots = this.querySelectorAll(".timeline-progress__dot");
    const progressBar = this.querySelector(".timeline-progress__progress");
    const line = this.querySelector(".timeline-progress__line");

    if (!dots.length || !progressBar || !line) return;

    const items = Array.from(dots)
      .map((dot) => ({
        date: new Date(dot.getAttribute("data-date") + "T00:00:00"),
        dot,
      }))
      .filter((item) => !isNaN(item.date))
      .sort((a, b) => a.date - b.date);

    if (!items.length) return;

    // Mark active dots
    dots.forEach((dot) => dot.classList.remove("active"));
    for (const item of items) {
      if (currentDate >= item.date) {
        item.dot.classList.add("active");
      }
    }

    // Mobile CSS stacks the items in a column; measure along that axis
    const track = this.querySelector(".timeline-progress__track");
    const vertical =
      track && getComputedStyle(track).flexDirection === "column";

    if (vertical) {
      // Run the line from the first dot's center to the last dot's center
      const trackTop = track.getBoundingClientRect().top;
      const centers = items.map((item) => this.getDotCenter(item.dot, true));
      const start = Math.min(...centers) - trackTop;
      const end = Math.max(...centers) - trackTop;
      this.style.setProperty("--timeline-line-start", `${start}px`);
      this.style.setProperty("--timeline-line-length", `${end - start}px`);
    } else {
      this.style.removeProperty("--timeline-line-start");
      this.style.removeProperty("--timeline-line-length");
    }

    const lineRect = line.getBoundingClientRect();
    const lineStart = vertical ? lineRect.top : lineRect.left;
    const lineLength = vertical ? lineRect.height : lineRect.width;
    if (lineLength <= 0) return;

    // Map each dot to its visual position as a fraction of the line
    const points = items.map((item) => ({
      pos: (this.getDotCenter(item.dot, vertical) - lineStart) / lineLength,
      time: item.date.getTime(),
    }));

    const now = currentDate.getTime();
    let fraction = 0;

    if (points.length === 1) {
      fraction = now >= points[0].time ? points[0].pos : 0;
    } else if (now >= points[points.length - 1].time) {
      // Past or at last dot — fill completely
      fraction = 1;
    } else if (now < points[0].time) {
      // Before first dot — extrapolate backwards using first segment's rate
      const rate =
        (points[1].pos - points[0].pos) / (points[1].time - points[0].time);
      fraction = points[0].pos + rate * (now - points[0].time);
    } else {
      // Between two dots — interpolate within the segment
      for (let i = 0; i < points.length - 1; i++) {
        if (now >= points[i].time && now < points[i + 1].time) {
          const t =
            (now - points[i].time) / (points[i + 1].time - points[i].time);
          fraction = points[i].pos + t * (points[i + 1].pos - points[i].pos);
          break;
        }
      }
    }

    // Store as a percentage of the line so the bar scales with the layout
    // between measurements instead of keeping a stale pixel width.
    const minLength = 24;
    const minFraction = Math.min(1, minLength / lineLength);
    const clamped = Math.min(1, Math.max(minFraction, fraction));
    const percent = `${(clamped * 100).toFixed(3)}%`;
    progressBar.style.width = vertical ? "" : percent;
    progressBar.style.height = vertical ? percent : "";
  }

  getDotCenter(dot, vertical = false) {
    const rect = dot.getBoundingClientRect();
    return vertical
      ? rect.top + rect.height / 2
      : rect.left + rect.width / 2;
  }
}

customElements.define("timeline-progress", TimelineProgress);
